import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Upload, Mail, AlertCircle, CheckCircle, Loader2, FileSpreadsheet, FolderPlus, Plus } from 'lucide-react'
import toast from 'react-hot-toast'
import { bulkEnrollRecipients, getRecipientGroups } from '../../services/api'

export default function EnrollRecipientsModal({ sequence, onClose, onSuccess }) {
  const queryClient = useQueryClient()
  const [emailsInput, setEmailsInput] = useState('')
  const [forceReset, setForceReset] = useState(false)
  const [parseError, setParseError] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [groupOption, setGroupOption] = useState('none') // 'none', 'existing', 'new'
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [newGroupName, setNewGroupName] = useState('')
  const fileInputRef = useRef(null)

  // Fetch existing groups
  const { data: groupsData } = useQuery({
    queryKey: ['recipientGroups'],
    queryFn: getRecipientGroups
  })
  const groups = groupsData?.member || groupsData?.['hydra:member'] || groupsData || []

  // Parse emails from textarea (comma, semicolon, newline, space separated)
  // Also supports CSV format: email,firstName,lastName
  const parseEmails = (text) => {
    const lines = text.split(/[\n\r]+/).map(line => line.trim()).filter(Boolean)
    const recipients = []
    const errors = []

    lines.forEach((line, index) => {
      // Try CSV format first (email,firstName,lastName)
      const parts = line.split(/[,;\t]/).map(p => p.trim())

      if (parts.length >= 1) {
        const email = parts[0].toLowerCase()

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
          // Maybe it's space-separated multiple emails on one line
          const spaceEmails = line.split(/[\s,;]+/).filter(e => emailRegex.test(e))
          if (spaceEmails.length > 0) {
            spaceEmails.forEach(e => {
              recipients.push({ email: e.toLowerCase() })
            })
          } else {
            errors.push(`Ligne ${index + 1}: "${email}" n'est pas un email valide`)
          }
          return
        }

        recipients.push({
          email,
          firstName: parts[1] || null,
          lastName: parts[2] || null
        })
      }
    })

    return { recipients, errors }
  }

  const { recipients: parsedRecipients, errors: parseErrors } = parseEmails(emailsInput)

  // Handle file drop/select
  const handleFile = (file) => {
    if (!file) return

    const validTypes = ['text/csv', 'text/plain', 'application/vnd.ms-excel']
    const validExtensions = ['.csv', '.txt']
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))

    if (!validTypes.includes(file.type) && !hasValidExtension) {
      toast.error('Format non supporte. Utilisez un fichier CSV ou TXT.')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result
      if (typeof content === 'string') {
        // Append to existing content or replace
        setEmailsInput(prev => prev ? prev + '\n' + content : content)
        toast.success(`Fichier "${file.name}" charge`)
      }
    }
    reader.onerror = () => {
      toast.error('Erreur lors de la lecture du fichier')
    }
    reader.readAsText(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set to false if leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget)) return
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      handleFile(files[0])
    }
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFile(file)
      // Reset input so same file can be selected again
      e.target.value = ''
    }
  }

  const mutation = useMutation({
    mutationFn: () => {
      const options = {
        forceReset,
        groupId: groupOption === 'existing' && selectedGroupId ? parseInt(selectedGroupId) : null,
        createGroupName: groupOption === 'new' && newGroupName.trim() ? newGroupName.trim() : null
      }
      return bulkEnrollRecipients(sequence.id, parsedRecipients, options)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sequenceEnrollments', sequence.id] })
      queryClient.invalidateQueries({ queryKey: ['recipientGroups'] })

      let message = `${data.enrolled} destinataire(s) inscrit(s)`
      if (data.reset > 0) message += `, ${data.reset} redemarré(s)`
      if (data.skipped > 0) message += `, ${data.skipped} ignoré(s)`
      if (data.group) message += ` (groupe: ${data.group.name})`

      toast.success(message, { id: 'enroll-success' })
      onSuccess?.()
      onClose()
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || err.message || 'Erreur lors de l\'inscription', { id: 'enroll-error' })
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (parsedRecipients.length === 0) {
      toast.error('Aucun email valide trouvé')
      return
    }
    mutation.mutate()
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: '#1a1a1a',
        borderRadius: '16px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Ajouter des destinataires
            </h2>
            <p style={{ color: '#888', fontSize: '0.9rem' }}>
              Séquence : {sequence.name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Instructions */}
          <div style={{
            background: 'rgba(59,130,246,0.1)',
            border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            fontSize: '0.85rem',
            color: '#93c5fd'
          }}>
            <strong>Formats acceptes :</strong>
            <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0 }}>
              <li>Un email par ligne</li>
              <li>Emails separes par virgule, point-virgule ou espace</li>
              <li>Format CSV : <code style={{ background: 'rgba(0,0,0,0.3)', padding: '0.1rem 0.3rem', borderRadius: '3px' }}>email,prenom,nom</code></li>
            </ul>
          </div>

          {/* Drop zone + Textarea */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem'
            }}>
              <label style={{
                fontWeight: 500,
                fontSize: '0.9rem'
              }}>
                Liste des emails
              </label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#888',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <FileSpreadsheet size={14} />
                Importer CSV
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
            </div>

            <div
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                position: 'relative',
                borderRadius: '8px',
                border: isDragging
                  ? '2px dashed #5b5cf4'
                  : '1px solid rgba(255,255,255,0.1)',
                background: isDragging
                  ? 'rgba(91, 92, 244,0.05)'
                  : 'rgba(255,255,255,0.05)',
                transition: 'all 0.2s ease'
              }}
            >
              {isDragging && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.8)',
                  borderRadius: '8px',
                  zIndex: 10,
                  pointerEvents: 'none'
                }}>
                  <FileSpreadsheet size={32} style={{ color: '#5b5cf4', marginBottom: '0.5rem' }} />
                  <span style={{ color: '#5b5cf4', fontWeight: 500 }}>
                    Deposez votre fichier CSV ici
                  </span>
                </div>
              )}
              <textarea
                value={emailsInput}
                onChange={(e) => setEmailsInput(e.target.value)}
                placeholder="Glissez-deposez un fichier CSV ici ou collez vos emails...&#10;&#10;exemple@email.com&#10;john@company.com,John,Doe&#10;jane@startup.io"
                rows={8}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* Preview */}
          {emailsInput.trim() && (
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.75rem',
                fontWeight: 500,
                fontSize: '0.9rem'
              }}>
                <Mail size={16} style={{ color: '#5b5cf4' }} />
                {parsedRecipients.length} email(s) valide(s) detecte(s)
              </div>

              {parseErrors.length > 0 && (
                <div style={{
                  color: '#f59e0b',
                  fontSize: '0.8rem',
                  marginBottom: '0.75rem'
                }}>
                  <AlertCircle size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                  {parseErrors.length} erreur(s) de format
                </div>
              )}

              {parsedRecipients.length > 0 && (
                <div style={{
                  maxHeight: '120px',
                  overflow: 'auto',
                  fontSize: '0.8rem',
                  color: '#888'
                }}>
                  {parsedRecipients.slice(0, 10).map((r, i) => (
                    <div key={i} style={{
                      padding: '0.25rem 0',
                      display: 'flex',
                      gap: '0.5rem'
                    }}>
                      <CheckCircle size={12} style={{ color: '#5b5cf4', flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        {r.email}
                        {(r.firstName || r.lastName) && (
                          <span style={{ color: '#666' }}>
                            {' '}({[r.firstName, r.lastName].filter(Boolean).join(' ')})
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                  {parsedRecipients.length > 10 && (
                    <div style={{ color: '#666', fontStyle: 'italic', marginTop: '0.5rem' }}>
                      ... et {parsedRecipients.length - 10} autre(s)
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Group assignment */}
          <div style={{
            marginBottom: '1rem',
            padding: '1rem',
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.75rem',
              fontWeight: 500,
              fontSize: '0.9rem'
            }}>
              <FolderPlus size={16} style={{ color: '#3b82f6' }} />
              Ajouter a un groupe (optionnel)
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setGroupOption('none')}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  background: groupOption === 'none' ? 'rgba(91, 92, 244,0.15)' : 'transparent',
                  border: groupOption === 'none' ? '1px solid #5b5cf4' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  color: groupOption === 'none' ? '#5b5cf4' : '#888',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                Aucun
              </button>
              <button
                type="button"
                onClick={() => setGroupOption('existing')}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  background: groupOption === 'existing' ? 'rgba(59,130,246,0.15)' : 'transparent',
                  border: groupOption === 'existing' ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  color: groupOption === 'existing' ? '#3b82f6' : '#888',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                Existant
              </button>
              <button
                type="button"
                onClick={() => setGroupOption('new')}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  background: groupOption === 'new' ? 'rgba(245,158,11,0.15)' : 'transparent',
                  border: groupOption === 'new' ? '1px solid #f59e0b' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  color: groupOption === 'new' ? '#f59e0b' : '#888',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.3rem'
                }}
              >
                <Plus size={12} />
                Nouveau
              </button>
            </div>

            {groupOption === 'existing' && (
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                <option value="">Selectionner un groupe...</option>
                {Array.isArray(groups) && groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({g.recipientCount || 0} destinataires)
                  </option>
                ))}
              </select>
            )}

            {groupOption === 'new' && (
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Nom du nouveau groupe..."
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.85rem'
                }}
              />
            )}
          </div>

          {/* Force reset checkbox */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            color: '#888'
          }}>
            <input
              type="checkbox"
              checked={forceReset}
              onChange={(e) => setForceReset(e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                accentColor: '#5b5cf4'
              }}
            />
            Redemarrer la sequence pour les destinataires deja inscrits
          </label>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff',
                padding: '0.75rem 1.25rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || parsedRecipients.length === 0}
              style={{
                background: parsedRecipients.length > 0 ? '#5b5cf4' : 'rgba(91, 92, 244,0.3)',
                color: '#0a0a0a',
                border: 'none',
                padding: '0.75rem 1.25rem',
                borderRadius: '8px',
                cursor: parsedRecipients.length > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.9rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Inscription...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Inscrire {parsedRecipients.length} destinataire(s)
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
